// CoachDashboardService — migrated to the OpenAPI-generated client. The weekly-summary endpoints
// (AdminCoachDashboardController + AdminProgramController) use FLAT page/size query params (not a nested
// Spring Pageable), so they map cleanly to the SDK query. The server derives the company from the
// caller's principal (IDOR sweep) — none of these endpoints accept a companyId param — so the legacy
// `companyId` argument is accepted for signature compatibility but NOT forwarded.
//
// Each function returns `{data}` where data is the flat Paged* body (callers read res.data.content /
// res.data.total / res.data.hasNext), and re-throws an axios-shaped `{response:{data,status}}` on error.

import {
  adminCoachDashboardControllerGetNutritionWeeklySummary,
  adminCoachDashboardControllerGetStepsWeeklySummary,
  adminCoachDashboardControllerGetWeightWeeklySummary,
  adminCoachDashboardControllerGetCheckInsWeeklySummary,
  adminCoachDashboardControllerGetClientsWeeklySummary,
  adminCoachDashboardControllerToggleClientReview,
  adminCoachDashboardControllerGetTraineeProfile,
  adminProgramControllerGetWeeklySummary,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

// Build the shared weekly-summary query, dropping null/empty values exactly like the old URL builder.
// `companyId` is intentionally omitted — the server scopes by principal.
const buildSummaryQuery = ({
  trainerId = null,
  keyword = "",
  page = 0,
  size = 25,
  sortKey = "name",
  sortDirection = "ASC",
  state = null,
  filter = null,
  weekOffset = 0,
}) => {
  const query = {page, size, sortKey, sortDirection, weekOffset};
  if (trainerId != null) query.trainerId = trainerId;
  if (keyword) query.keyword = keyword;
  if (state) query.state = state;
  if (filter && filter !== "all") query.filter = filter;
  return query;
};

export const getWeeklyNutritionSummary = async (args = {}) => {
  const {data, error, response} = await adminCoachDashboardControllerGetNutritionWeeklySummary({
    query: buildSummaryQuery(args),
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getWeeklyStepsSummary = async (args = {}) => {
  const {data, error, response} = await adminCoachDashboardControllerGetStepsWeeklySummary({
    query: buildSummaryQuery(args),
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getWeeklyWeightSummary = async (args = {}) => {
  const {data, error, response} = await adminCoachDashboardControllerGetWeightWeeklySummary({
    query: buildSummaryQuery(args),
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getWeeklyCheckInSummary = async (args = {}) => {
  const {data, error, response} = await adminCoachDashboardControllerGetCheckInsWeeklySummary({
    query: buildSummaryQuery(args),
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getWeeklyClientsSummary = async ({
  trainerId = null,
  keyword = "",
  page = 0,
  size = 25,
  sortKey = "name",
  sortDirection = "ASC",
  state = null,
  closeToEndDays = null,
  weekOffset = 0,
} = {}) => {
  // The clients tab uses closeToEndDays instead of the `filter` param the other tabs use.
  const query = {page, size, sortKey, sortDirection, weekOffset};
  if (trainerId != null) query.trainerId = trainerId;
  if (keyword) query.keyword = keyword;
  if (state) query.state = state;
  if (closeToEndDays != null) query.closeToEndDays = closeToEndDays;

  const {data, error, response} = await adminCoachDashboardControllerGetClientsWeeklySummary({query});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const toggleClientReview = async (traineeId) => {
  const {data, error, response} = await adminCoachDashboardControllerToggleClientReview({
    path: {traineeId},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getTraineeProfile = async (traineeId, weekOffset = 0, range = "month") => {
  const {data, error, response} = await adminCoachDashboardControllerGetTraineeProfile({
    path: {traineeId},
    query: {weekOffset, range},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getWeeklyWorkoutSummary = async (args = {}) => {
  const {data, error, response} = await adminProgramControllerGetWeeklySummary({
    query: buildSummaryQuery(args),
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};
